import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Container,
  Grid,
  Button,
} from '@material-ui/core';
import { ArrowForward as ArrowForwardIcon } from '@material-ui/icons';
import { useSelector } from 'react-redux';
import { ROUTES } from '../../../../../app/routes/routeMap';
import InfoBlock from '../../../components/common/InfoBlock';
import IntlMessages from 'util/IntlMessages';

/**
 * Results Page Integration Step — pointer-only step.
 *
 * As of the "UI Settings consolidation", the URL + container-selector
 * controls for the Results Page have moved out of the wizard and into
 * the operator-facing UI Settings surface. This step now only:
 *   - tells the user where the controls have moved,
 *   - keeps the wizard progression contract (onReady, onRegisterSave),
 *   - does NOT touch the legacy `url` / `container` payload.
 *
 * Edge cases handled:
 *   1. Users who already saved a `url` / `container` before the migration:
 *      the next publish keeps those values untouched because
 *      `updateViewIntegration` is no longer called here. Their existing
 *      values remain the source of truth until the user re-enters them
 *      through UI Settings.
 *   2. The wizard previously gated "Next" on both `url` and `container`
 *      being non-empty. That gate is removed — the step is now always
 *      ready so users can move on. Empty values are surfaced as a
 *      warning inside UI Settings instead of blocking the wizard.
 *   3. The step still calls `onRegisterSave(noop)` so the wizard transport
 *      contract (StepComponent.onRegisterSave) stays intact for downstream
 *      consumers (Summary, publish flow, E2E test harness).
 *   4. `normalizeUrl` previously stripped the query string and forced a
 *      leading slash. The pointer does NOT mutate the value — the UI
 *      Settings editor now owns that normalization so the wizard cannot
 *      accidentally rewrite a saved URL on Next-click.
 */
const ResultsPageIntegrationStep = ({
  onReady,
  onRegisterSave,
  title,
  shopId,
}) => {
  const currentShop = useSelector((s) => s.app.currentShop);
  const effectiveShopId = shopId || currentShop;

  useEffect(() => {
    if (onReady) onReady(true);
  }, [onReady]);

  const noopSave = useCallback(async () => {}, []);
  useEffect(() => {
    if (onRegisterSave) {
      onRegisterSave(noopSave);
    }
  }, [onRegisterSave, noopSave]);

  const settingsHref = effectiveShopId
    ? ROUTES.searchSettings(effectiveShopId, 'results-page')
    : null;

  return (
    <Container maxWidth="lg" style={{ height: '100%', paddingTop: 32, paddingBottom: 32 }}>
      <Grid container spacing={4} justify="center" style={{ height: '100%' }}>
        <Grid item xs={12} md={8} style={{ height: '100%' }}>
          <Paper
            elevation={1}
            data-testid="resultspage-integration-pointer"
            style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          >
            <Box p={3} display="flex" flexDirection="column" height="100%">
              <Box mb={3}>
                <Typography variant="h4" component="h2">
                  {title || <IntlMessages id="search.settings.resultsPage.integrationTitle" />}
                </Typography>
              </Box>
              <Divider />

              <Box flex={1} display="flex" flexDirection="column" justifyContent="center" alignItems="center" textAlign="center" p={4}>
                <InfoBlock
                  title={<IntlMessages id="ui.integrationWizard.embedded.resultsPageIntegrationStep.urlAndContainerMoved" />}
                  description={<IntlMessages id="ui.integrationWizard.embedded.resultsPageIntegrationStep.urlAndResultsContainerConfigurationForTheResultsPageIsNow" />}
                />

                {settingsHref ? (
                  <Box mt={3}>
                    <Button
                      component={RouterLink}
                      to={settingsHref}
                      variant="contained"
                      color="primary"
                      endIcon={<ArrowForwardIcon />}
                      data-testid="resultspage-integration-open-settings-btn"
                    >
                      <IntlMessages id="ui.integrationWizard.embedded.resultsPageIntegrationStep.openUiSettings" />
                    </Button>
                  </Box>
                ) : (
                  <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
                    <IntlMessages id="ui.integrationWizard.embedded.resultsPageIntegrationStep.selectAShopToOpenUiSettings" />
                  </Typography>
                )}

                <Typography variant="caption" color="textSecondary" style={{ marginTop: 24 }}>
                  <IntlMessages id="ui.integrationWizard.embedded.resultsPageIntegrationStep.path" />
                  {' '}
                  <code>UI Settings → Results Page → Integration → URL & Container</code>
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

ResultsPageIntegrationStep.propTypes = {
  onReady: PropTypes.func,
  onRegisterSave: PropTypes.func,
  title: PropTypes.string,
  shopId: PropTypes.string,
};

ResultsPageIntegrationStep.defaultProps = {
  onReady: null,
  onRegisterSave: null,
  title: "ui.integrationWizard.embedded.resultsPageIntegrationStep.resultsPageIntegration",
  shopId: null,
};

export default ResultsPageIntegrationStep;
